import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { PageHeader } from '@/components/brand/PageHeader';
import { Panel } from '@/components/brand/Panel';
import { SevPill } from '@/components/brand/SevPill';
import { useApiQuery } from '@/lib/hooks';
import { ErrorState, LoadingState, UnauthorizedState } from '@/components/brand/States';
import { useAuth } from '@/lib/auth';

interface HistoryDetail {
  id: string;
  ruleId: string;
  state: 'triggered' | 'resolved';
  triggeredAt: number;
  resolvedAt: number | null;
  acknowledgedAt: number | null;
  acknowledgedBy: string | null;
  escalatedAt: number | null;
  message: string;
  notifiedChannelsJson: string;
  contextJson: string;
  rule?: {
    id: string;
    name: string;
    ruleType: string;
    severity: 'info' | 'warning' | 'critical';
    source: string | null;
    threshold: number;
    windowMinutes: number;
  };
}

export function AlertHistoryDetailPage({ id }: { id: string }) {
  const { isUnauthorized, me } = useAuth();
  const qc = useQueryClient();
  const [snoozeMinutes, setSnoozeMinutes] = useState(60);
  const [snoozeReason, setSnoozeReason] = useState('');

  const q = useApiQuery<HistoryDetail>({
    queryKey: ['alerts/history', id],
    queryFn: async () => {
      const res = await fetch(`/api/alerts/history/${encodeURIComponent(id)}`, { credentials: 'include' });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      return res.json();
    },
  });

  const ackMutation = useMutation({
    mutationFn: async () => {
      const res = await fetch(`/api/alerts/history/${encodeURIComponent(id)}/ack`, {
        method: 'POST',
        credentials: 'include',
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}: ${await res.text()}`);
      return res.json();
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['alerts/history', id] }),
  });

  const snoozeMutation = useMutation({
    mutationFn: async () => {
      const ruleId = q.data?.ruleId;
      if (!ruleId) throw new Error('rule id unknown');
      const res = await fetch('/api/alerts/snooze', {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          rule_id: ruleId,
          minutes: snoozeMinutes,
          reason: snoozeReason || undefined,
        }),
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}: ${await res.text()}`);
      return res.json();
    },
    onSuccess: () => {
      setSnoozeReason('');
      qc.invalidateQueries({ queryKey: ['alerts/history', id] });
    },
  });

  if (isUnauthorized) return <UnauthorizedState />;
  if (q.isLoading) return <LoadingState />;
  if (q.error) return <ErrorState title="Couldn't load alert" detail={(q.error as Error).message} />;
  if (!q.data) return <ErrorState title="Alert not found" />;

  const h = q.data;
  const channels = (() => {
    try { return JSON.parse(h.notifiedChannelsJson) as string[]; } catch { return []; }
  })();
  const context = (() => {
    try { return JSON.parse(h.contextJson) as Record<string, unknown>; } catch { return {}; }
  })();
  const sev = h.rule?.severity ?? 'warning';

  return (
    <div className="space-y-5">
      <PageHeader
        eyebrow="Alert"
        title={h.rule?.name ?? `Alert ${id.slice(0, 8)}`}
        meta={<SevPill level={sev === 'critical' ? 'crit' : sev === 'warning' ? 'warn' : 'info'} label={sev} />}
      />
      <Panel title="State">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-small">
          <div>
            <p className="eyebrow mb-1">State</p>
            {h.state === 'resolved' ? (
              <SevPill level="ok" label="resolved" />
            ) : h.acknowledgedAt ? (
              <SevPill level="warn" label="acknowledged" />
            ) : (
              <SevPill level="crit" label="open" />
            )}
          </div>
          <div>
            <p className="eyebrow mb-1">Triggered</p>
            <p className="mono mono-sm">{new Date(h.triggeredAt).toISOString()}</p>
          </div>
          <div>
            <p className="eyebrow mb-1">Resolved</p>
            <p className="mono mono-sm">{h.resolvedAt ? new Date(h.resolvedAt).toISOString() : '—'}</p>
          </div>
          <div>
            <p className="eyebrow mb-1">Acknowledged</p>
            <p className="mono mono-sm">
              {h.acknowledgedAt ? `${new Date(h.acknowledgedAt).toISOString()} by ${h.acknowledgedBy}` : '—'}
            </p>
          </div>
        </div>
        {!h.acknowledgedAt && h.state === 'triggered' && (
          <div className="mt-5 flex flex-wrap items-end gap-3">
            <button
              type="button"
              disabled={ackMutation.isPending}
              onClick={() => ackMutation.mutate()}
              className="bg-fg-default text-mb-spring-wood px-4 py-2 text-eyebrow disabled:opacity-40"
            >
              {ackMutation.isPending ? 'Acknowledging…' : 'Acknowledge'}
            </button>
            {me?.role === 'admin' && (
              <div className="flex items-end gap-2 border-l border-rule pl-4">
                <label className="flex flex-col gap-1 text-small">
                  <span className="eyebrow">Snooze min</span>
                  <input
                    type="number"
                    value={snoozeMinutes}
                    min={1}
                    onChange={(e) => setSnoozeMinutes(Number(e.target.value))}
                    className="w-24 border border-rule px-2 py-1 bg-bg-page num"
                  />
                </label>
                <label className="flex flex-col gap-1 text-small flex-1 min-w-[200px]">
                  <span className="eyebrow">Reason (optional)</span>
                  <input
                    type="text"
                    value={snoozeReason}
                    onChange={(e) => setSnoozeReason(e.target.value)}
                    placeholder="e.g. planned maintenance window"
                    className="border border-rule px-2 py-1 bg-bg-page"
                  />
                </label>
                <button
                  type="button"
                  disabled={snoozeMutation.isPending}
                  onClick={() => snoozeMutation.mutate()}
                  className="bg-bg-page border border-rule px-4 py-2 text-eyebrow disabled:opacity-40"
                >
                  {snoozeMutation.isPending ? 'Snoozing…' : `Snooze rule ${snoozeMinutes}m`}
                </button>
              </div>
            )}
            {(ackMutation.error || snoozeMutation.error) && (
              <p className="text-sev-crit text-caption mono w-full">
                {(ackMutation.error || snoozeMutation.error)?.message}
              </p>
            )}
            {snoozeMutation.isSuccess && (
              <p className="text-sev-ok text-caption mono w-full">Rule snoozed.</p>
            )}
          </div>
        )}
      </Panel>
      <Panel title="Message">
        <p className="text-body">{h.message}</p>
      </Panel>
      {h.rule && (
        <Panel title="Rule">
          <dl className="grid grid-cols-2 md:grid-cols-3 gap-4 text-small">
            <div><dt className="eyebrow mb-1">Type</dt><dd className="mono">{h.rule.ruleType}</dd></div>
            <div><dt className="eyebrow mb-1">Source</dt><dd className="mono">{h.rule.source ?? '—'}</dd></div>
            <div><dt className="eyebrow mb-1">Threshold</dt><dd className="num">{h.rule.threshold}</dd></div>
            <div><dt className="eyebrow mb-1">Window</dt><dd className="num">{h.rule.windowMinutes} min</dd></div>
          </dl>
        </Panel>
      )}
      <Panel title="Notifications">
        {channels.length === 0 ? (
          <p className="text-fg-muted text-small">No channels notified (info severity or notification worker not configured).</p>
        ) : (
          <ul className="flex gap-2 flex-wrap">
            {channels.map((c) => (
              <li key={c} className="border border-rule px-2 py-1 text-caption mono">{c}</li>
            ))}
          </ul>
        )}
      </Panel>
      <Panel title="Context">
        <pre className="mono mono-sm text-fg-muted overflow-x-auto">{JSON.stringify(context, null, 2)}</pre>
      </Panel>
    </div>
  );
}
